import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';

// Import components
import Dashboard from './components/Dashboard/Dashboard';
import VoiceRecorder from './components/VoiceRecording/VoiceRecorder';
import TemplateManager from './components/Templates/TemplateManager';
import Settings from './components/Settings/Settings';
import AuthForm from './components/Authentication/AuthForm';
import SubscriptionPlans from './components/Subscription/SubscriptionPlans';
import SubscriptionSuccess from './components/Subscription/SubscriptionSuccess';
import BAAAcceptance from './components/Legal/BAAAcceptance';
import ChartSelection from './components/Analytics/ChartSelection';
import TreatmentPlanReview from './components/Analytics/TreatmentPlanReview';
import CaseloadDiagnosis from './components/Analytics/CaseloadDiagnosis';
import IndividualPHQ9Tracker from './components/Analytics/IndividualPHQ9Tracker';
import IndividualGAD7Tracker from './components/Analytics/IndividualGAD7Tracker';
import NoShowAnalysis from './components/Analytics/NoShowAnalysis';
import IncomeByMonth from './components/Analytics/IncomeByMonth';
import SupervisorDashboard from './components/Supervisor/SupervisorDashboard';

// Import UI components
import LoadingSpinner from './components/UI/LoadingSpinner';
import Header from './components/UI/Header';
import Sidebar from './components/UI/Sidebar';

// Import services
import { Hub } from 'aws-amplify/utils';
import { cognitoAuthService } from './services/cognitoAuthService';
import { hipaaService } from './services/hipaaComplianceService';
import { webStorageService } from './services/webStorageService';
import { usageTrackingService } from './services/usageTrackingService';
import { baaService } from './services/baaService';
import { orgService } from './services/orgService';

// After sign-in, send the user to the destination they originally deep-linked
// to (e.g. /subscription/plans from the marketing site). Needed because Google
// SSO always returns to the site root, losing the intended path.
function PostAuthRedirect() {
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    const dest = sessionStorage.getItem('postAuthDest');
    if (dest) {
      sessionStorage.removeItem('postAuthDest');
      const current = location.pathname + location.search;
      if (dest !== current) navigate(dest, { replace: true });
    }
  }, [navigate, location]);
  return null;
}

function App() {
  // Application state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [baaAccepted, setBAAAccepted] = useState(false);
  const [checkingBAA, setCheckingBAA] = useState(false);
  const [isSupervisor, setIsSupervisor] = useState(false);

  // Preserve a deep-link destination (e.g. /subscription/plans from the site)
  // across passwordless / Google sign-up so the user lands where they intended.
  useEffect(() => {
    const path = window.location.pathname;
    if (path && path !== '/' && !path.startsWith('/subscription/success')) {
      try { sessionStorage.setItem('postAuthDest', path + window.location.search); } catch {}
    }
  }, []);

  // Determine whether the signed-in user is a practice administrator
  // (drives the Supervisor nav item + /supervisor route). Never blocks the app.
  useEffect(() => {
    let cancelled = false;
    if (currentUser) {
      orgService.getMe()
        .then(r => { if (!cancelled) setIsSupervisor(r?.role === 'admin'); })
        .catch(() => { if (!cancelled) setIsSupervisor(false); });
    } else {
      setIsSupervisor(false);
    }
    return () => { cancelled = true; };
  }, [currentUser]);

  // Initialize app and check authentication
  useEffect(() => {
    // AUTO-LINK retry (URL-based): the first Google sign-in for an existing
    // account intentionally aborts while the PreSignUp Lambda links the
    // identities, bouncing back with this error in the URL. Detect it here
    // at load (the Hub failure event can fire before listeners attach) and
    // silently retry once — the retry signs in as the linked account.
    const params = new URLSearchParams(window.location.search);
    const oauthErrorDesc = params.get('error_description') || '';
    if (oauthErrorDesc.includes('ACCOUNT_LINKED_RETRY_SIGNIN')) {
      window.history.replaceState({}, '', window.location.pathname); // clean URL
      if (!sessionStorage.getItem('sso_link_retry')) {
        sessionStorage.setItem('sso_link_retry', '1');
        cognitoAuthService.signInWithGoogle().catch((e) => {
          console.error('Google sign-in retry failed:', e);
          setIsLoading(false);
        });
        return; // redirecting away — skip normal init
      }
      sessionStorage.removeItem('sso_link_retry');
    }

    // OAuth return (?code=...): Amplify exchanges the code for tokens
    // asynchronously — a single immediate session check can lose that race
    // and strand a signed-in user on the login page. Poll briefly instead.
    initializeApp(params.has('code') ? 20 : 0);
    
    // HIPAA Compliance: Set up session timeout listener
    const handleSessionTimeout = () => {
      handleLogout();
      alert('Your session has expired for security reasons. Please log in again.');
    };
    
    window.addEventListener('hipaa-session-timeout', handleSessionTimeout);

    // Complete Google SSO when the browser returns from the redirect.
    // Amplify finishes the token exchange asynchronously and emits these
    // Hub events; re-initialize so the session is picked up.
    const unsubscribeHub = Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'signInWithRedirect') {
        sessionStorage.removeItem('sso_link_retry'); // reset auto-link retry guard
        initializeApp().then(() => {
          // HIPAA Compliance: audit the SSO login (initializeApp doesn't log
          // LOGIN because it also runs on ordinary page refreshes)
          const u = cognitoAuthService.getCurrentUser();
          if (u) hipaaService.logAuthEvent('LOGIN', u.email);
        });
      } else if (payload.event === 'signInWithRedirect_failure') {
        // AUTO-LINK: the FIRST Google sign-in for an email that already has
        // a native account intentionally aborts while the PreSignUp Lambda
        // links the identities. Retry once, silently — the retry signs in
        // as the linked account with all existing data.
        if (!sessionStorage.getItem('sso_link_retry')) {
          sessionStorage.setItem('sso_link_retry', '1');
          cognitoAuthService.signInWithGoogle().catch((e) => {
            console.error('Google sign-in retry failed:', e);
            setIsLoading(false);
          });
        } else {
          sessionStorage.removeItem('sso_link_retry');
          console.error('Google sign-in failed:', payload.data);
          setIsLoading(false);
        }
      }
    });

    return () => {
      window.removeEventListener('hipaa-session-timeout', handleSessionTimeout);
      unsubscribeHub();
    };
  }, []);

  const initializeApp = async (sessionRetries = 0) => {
    try {
      // Check if user is already authenticated. When returning from an
      // OAuth redirect (sessionRetries > 0), the token exchange may still
      // be in flight — poll every 500ms until it lands or retries run out.
      let user = null;
      for (let attempt = 0; ; attempt++) {
        await cognitoAuthService.initializeFromSession();
        user = cognitoAuthService.getCurrentUser();
        if (user || attempt >= sessionRetries) break;
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      if (user) {
        setCurrentUser(user);
        setIsAuthenticated(true);
        // Set user ID for data isolation
        webStorageService.setCurrentUser(user.id);
        // Initialize usage tracking for the user
        usageTrackingService.setCurrentUser(user.id);
        // Check BAA acceptance
        await checkBAAStatus(user);
      }
    } catch (error) {
      console.error('Failed to initialize app:', error);
      // Continue with unauthenticated state
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async (user) => {
    // AuthForm handles the complete authentication flow and passes the final user object
    try {
      setCurrentUser(user);
      setIsAuthenticated(true);
      // Set user ID for data isolation
      webStorageService.setCurrentUser(user.id);
      // Initialize usage tracking for the user
      usageTrackingService.setCurrentUser(user.id);
      // HIPAA Compliance: Log authentication — fires for all login paths including first login
      hipaaService.logAuthEvent('LOGIN', user.email);
      // Check BAA acceptance
      await checkBAAStatus(user);
    } catch (error) {
      console.error('Login completion failed:', error);
      throw error;
    }
  };

  const checkBAAStatus = async (user) => {
    try {
      setCheckingBAA(true);
      const hasAccepted = await baaService.hasBAAAccepted(user);
      const needsReacceptance = await baaService.needsBAAReacceptance(user);
      setBAAAccepted(hasAccepted && !needsReacceptance);
    } catch (error) {
      console.error('Error checking BAA status:', error);
      setBAAAccepted(false); // Err on the side of caution
    } finally {
      setCheckingBAA(false);
    }
  };

  const handleBAAAcceptance = async () => {
    try {
      await baaService.acceptBAA(currentUser);
      setBAAAccepted(true);
    } catch (error) {
      console.error('Error accepting BAA:', error);
      throw error;
    }
  };

  const handleLogout = async () => {
    try {
      // DON'T clear BAA acceptance on normal logout - keep it for next session
      // BUT DO clear any clinical notes for HIPAA compliance
      if (currentUser) {
        // Clear any legacy persisted clinical notes on logout for HIPAA compliance.
        // Notes are session-only and never persisted; this clears any pre-existing cached copies.
        try {
          localStorage.removeItem(`chartara_secure_last_completed_note_user_${currentUser.id}`);
          localStorage.removeItem('recentNotes');
          localStorage.removeItem('voiceRecordings');
        } catch (e) {
          // no-op: cleanup is best-effort
        }

        hipaaService.logAuditEvent({
          action: 'USER_LOGOUT',
          details: `User logged out - BAA preserved, clinical notes cleared for HIPAA compliance`,
          level: 'SECURITY',
          userId: currentUser.id,
          userEmail: currentUser.email
        });
      }

      await cognitoAuthService.logout();
      setCurrentUser(null);
      setIsAuthenticated(false);
      setBAAAccepted(false);
      // Clear user ID for data isolation
      webStorageService.setCurrentUser(null);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  // Show loading screen while initializing
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-purple-900">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  // Show login screen if not authenticated (except subscription success page)
  if (!isAuthenticated) {
    if (window.location.pathname === '/subscription/success') {
      return (
        <Router>
          <Routes>
            <Route path="/subscription/success" element={<SubscriptionSuccess />} />
          </Routes>
        </Router>
      );
    }
    return <AuthForm onLogin={handleLogin} />;
  }

  // Show BAA acceptance screen if not accepted
  if (isAuthenticated && !baaAccepted && !checkingBAA) {
    return (
      <BAAAcceptance
        onAccept={handleBAAAcceptance}
        userEmail={currentUser?.email}
      />
    );
  }

  // Show loading screen while checking BAA
  if (checkingBAA) {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-purple-900">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  // Main authenticated app - With Header and Sidebar layout
  return (
    <Router>
      <Toaster position="top-right" />
      <PostAuthRedirect />
      <div className="min-h-screen bg-gray-50 flex">
        {/* Sidebar */}
        <Sidebar
          currentUser={currentUser}
          isSupervisor={isSupervisor}
        />
        
        {/* Main Content Area */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Header */}
          <Header 
            user={currentUser}
            onLogout={handleLogout}
          />
          
          {/* Page Content */}
          <main className="flex-1 overflow-hidden">
            <Routes>
              {/* Dashboard - Default route */}
              <Route path="/" element={<Dashboard user={currentUser} onLogout={handleLogout} />} />
              
              {/* Voice Recording */}
              <Route path="/record" element={<VoiceRecorder user={currentUser} />} />
              
              {/* Template Management */}
              <Route path="/templates" element={<TemplateManager user={currentUser} />} />

              {/* Visual Analytics */}
              <Route path="/analytics" element={<ChartSelection user={currentUser} />} />
              <Route path="/analytics/treatment-plan-review" element={<TreatmentPlanReview user={currentUser} />} />
              <Route path="/analytics/caseload-diagnosis" element={<CaseloadDiagnosis user={currentUser} />} />
              <Route path="/analytics/individual-phq9-tracker" element={<IndividualPHQ9Tracker user={currentUser} />} />
              <Route path="/analytics/individual-gad7-tracker" element={<IndividualGAD7Tracker user={currentUser} />} />
              <Route path="/analytics/no-show-analysis" element={<NoShowAnalysis user={currentUser} />} />
              <Route path="/analytics/income-by-month" element={<IncomeByMonth user={currentUser} />} />

              {/* Supervisor (practice administrator) — component enforces access server-side */}
              <Route path="/supervisor" element={<SupervisorDashboard user={currentUser} />} />

              {/* Subscription Management */}
              <Route path="/subscription/plans" element={<SubscriptionPlans />} />
              <Route path="/subscription/success" element={<SubscriptionSuccess />} />
              
              {/* Redirect any unknown routes to dashboard */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </main>
        </div>
      </div>
    </Router>
  );
}

export default App;